import { toast } from "react-toastify";
import type { CountryFeatures } from "types/countryFeatures";

type TFunction = (key: string, options?: Record<string, unknown>) => string;

export function validateCompanyInfoBeforeSubmit(
    {
        isCompanyEdited,
        isBankEdited,
        siret,
        bankInfo,
    }: {
        isCompanyEdited: boolean;
        isBankEdited: boolean;
        siret: string | null | undefined;
        bankInfo: Record<string, string>;
    },
    countryFeatures: CountryFeatures,
    t: TFunction,
): boolean {
    if (isCompanyEdited && siret) {
        const siretLength = countryFeatures.validation.siretLength;
        if (siretLength) {
            const digits = siret.replace(/[\s.-]/g, "").replace(/\D/g, "");
            if (digits.length !== siretLength) {
                toast.error(`${t("Settings.SiretInvalidLength", { count: siretLength })}`);
                return true;
            }
        }
    }

    const usesIbanBanking = countryFeatures.bankingFields.includes("iban");

    if (isBankEdited && !usesIbanBanking) {
        const bankingValidation = countryFeatures.validation.bankingFields;
        if (bankingValidation) {
            const bankFieldLabels: Record<string, string> = {
                sort_code: t("Beneficiary.sort_code"),
                account_number: t("Beneficiary.account_number"),
                transit_number: t("Beneficiary.transit_number"),
                institution_number: t("Beneficiary.institution_number"),
                bacs_sun: t("Company.bacs_sun"),
            };
            for (const [fieldKey, rule] of Object.entries(bankingValidation)) {
                const rawValue = bankInfo[fieldKey] ?? "";
                if (!rawValue) continue;
                const digits = rawValue.replace(/[-\s]/g, "").replace(/\D/g, "");
                const label = bankFieldLabels[fieldKey] || fieldKey;
                const exactLength = (rule as { length?: number }).length;
                const minLength = (rule as { minLength?: number }).minLength;
                const maxLength = (rule as { maxLength?: number }).maxLength;
                if (exactLength != null) {
                    if (digits.length !== exactLength) {
                        toast.error(`${t("Settings.BankFieldExactLength", { field: label, count: exactLength })}`);
                        return true;
                    }
                } else if (minLength != null || maxLength != null) {
                    if (
                        (minLength != null && digits.length < minLength) ||
                        (maxLength != null && digits.length > maxLength)
                    ) {
                        toast.error(
                            `${t("Settings.BankFieldLengthRange", { field: label, min: minLength, max: maxLength })}`,
                        );
                        return true;
                    }
                }
            }
        }
    }

    return false;
}
